import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { OrderformCartProductItem } from '../../../fascades/product-list.fascade';
import { ReplaySubject, Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';
import { OrderformOrderProductItem } from '@orderform/widgets';

export const QUANTITY_INPUT_SELECT_LIMIT_DESKTOP = 20;
export const QUANTITY_INPUT_SELECT_ITEM_COUNT_DESKTOP = 10;

if (typeof globalThis.window !== 'undefined' && !(globalThis.window as any).VUE_RADIO_BUTTON_GROUPS) {
  (globalThis.window as any).VUE_RADIO_BUTTON_GROUPS = {};
}

@Component({
  selector: 'orderform-quantity-input',
  templateUrl: './quantity-input.component.html',
  styleUrls: ['./quantity-input.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: QuantityInputComponent,
      multi: true,
    },
  ],
})
export class QuantityInputComponent implements OnInit, OnDestroy, OnChanges, ControlValueAccessor {
  @ViewChild('inputElement') inputElement!: ElementRef<HTMLInputElement>;

  ngAfterViewInit() {
    if (this.inputElement && this.inputElement.nativeElement.classList.contains('dynamic-input')) {
      this.adjustWidth(this.inputElement.nativeElement);
    }
  }

  @HostListener('input', ['$event'])
  onInput(event: InputEvent) {
    const input = event.target as HTMLInputElement;
    if (input.classList.contains('dynamic-input')) {
      this.adjustWidth(input);
    }
  }

  adjustWidth(input: HTMLInputElement) {
    const value = input.value;
    const width = Math.max(57, value.length * 10 + 25);
    input.style.width = `${width}px`;
  }

  private _destroy$ = new Subject<void>();

  public get nativeElement(): HTMLElement | null {
    return this._element.nativeElement;
  }

  static readonly RADIO_BUTTON_GROUP = (globalThis.window as any).VUE_RADIO_BUTTON_GROUPS || {};

  @Input() product: OrderformCartProductItem;
  @Input() radioButtonGroupName = 'none';
  @Input() showQuantitySelectLabel = false;
  @Input() mainProduct: OrderformOrderProductItem;
  @Input() showPreQuantity = false;

  selectLimit: number;
  showSelect: boolean;
  renderType: 'none' | 'display' | 'radiobutton' | 'checkbox' | 'input';
  isReadonly: boolean;
  forceNumberInput = false;

  @ViewChild('select', { static: false })
  private _selectElement: ElementRef<HTMLSelectElement>;
  @ViewChild('inputElement', { static: false })
  private _inputElement: ElementRef<HTMLInputElement>;

  public _value: number;
  private _internalMax: number;
  selectValues: { value: number | 'plus'; label: string }[];
  checkboxState$ = new ReplaySubject<boolean>(1);

  inputChange$ = new Subject<number>();

  constructor(private _cd: ChangeDetectorRef, private _element: ElementRef) {}

  /**
   * Standard Accessor methods for changes
   */
  propagateChange = (_: any) => {};

  /**
   * Standard Accessor methods for changes
   */
  propagateTouch = () => {};

  ngOnInit(): void {
    this.initializeRenderType();
    this.initializeMax();
    this.initializeSelectValues();

    this.inputChange$.pipe(debounceTime(300), distinctUntilChanged(), takeUntil(this._destroy$)).subscribe((amount) => {
      this.onChangeValue(amount);
    });
  }

  private isMainProduct() {
    return '' + this.mainProduct.product_id === '' + this.product.product_id;
  }

  private initializeSelectValues() {
    this.selectValues = [];
    this.selectLimit =
      this._internalMax > QUANTITY_INPUT_SELECT_LIMIT_DESKTOP
        ? QUANTITY_INPUT_SELECT_ITEM_COUNT_DESKTOP
        : this._internalMax;

    for (let i = this.product.quantity.min_value; i <= this.selectLimit; i++) {
      this.selectValues.push({ value: i, label: '' + i });
    }

    if (this.selectLimit < this._internalMax) {
      this.selectValues.push({ value: 'plus', label: this.selectLimit + '+' });
    }

    this.updateShowSelect();
  }

  private initializeMax() {
    this._internalMax = this.product.quantity.max_value;

    // set max qty to current qty of main product
    if (this.product.quantity.max_type === 'like_main_item') {
      this._internalMax = parseInt('' + this.mainProduct.quantity.value, 10);
    }

    if (!this._internalMax) {
      this._internalMax = 1000 + 1000 * Math.ceil(Math.max(this.product.quantity.min_value, this._value) / 1000);
    }
  }

  private initializeRenderType() {
    const minQuantity = this.product.quantity.min_value;
    const maxQuantity = this.product.quantity.max_value;

    const isRadioButton =
      (this.isMainProduct() && this.product.is_optional_if_addons_present === 'radio_button') ||
      (!this.isMainProduct() && this.product.quantity.max_type === 'radio_button');

    this.isReadonly = (minQuantity ? minQuantity === maxQuantity : false) || !this.product.quantity.is_editable;

    if (isRadioButton && !this.isReadonly) {
      if (!QuantityInputComponent.RADIO_BUTTON_GROUP[this.radioButtonGroupName]) {
        QuantityInputComponent.RADIO_BUTTON_GROUP[this.radioButtonGroupName] = {
          initialized: this.product.product_id,
          listener: {},
        };
      }
      QuantityInputComponent.RADIO_BUTTON_GROUP[this.radioButtonGroupName].listener['' + this.product.product_id] =
        this.onRadioGroupChange.bind(this);

      this._internalMax = 1;
      this.renderType = 'radiobutton';
    } else if (this.isReadonly && maxQuantity !== 1) {
      this.renderType = 'display';
    } else if (maxQuantity === 1) {
      this.renderType = 'checkbox';
    } else {
      this.renderType = 'input';
    }
  }

  registerOnChange(fn: any): void {
    this.propagateChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.propagateTouch = fn;
  }

  writeValue(value: number): void {
    value = parseInt((value || 0).toString(), 10);
    if (value === this._value) {
      return;
    }

    this._value = value;
    this.checkboxState$.next(value === 1);

    if (
      [
        this._selectElement ? (this._selectElement.nativeElement as Element) : null,
        this._inputElement ? this._inputElement.nativeElement : null,
      ].indexOf(document.activeElement) < 0
    ) {
      this.updateShowSelect();
    }
  }

  onChangeValue(value: number | 'plus') {
    if ('' + value === '') {
      return;
    }

    if (value === 'plus') {
      this._value = this.selectLimit;
      this.forceNumberInput = true;
      this.updateShowSelect();
    } else {
      this.forceNumberInput = false;
      this._value = value;
    }

    this.checkboxState$.next(this._value > 0);
    this.propagateChange(this._value);
  }

  onInputFocusOut() {
    if (this._inputElement?.nativeElement?.value !== this._value.toString()) {
      this._inputElement.nativeElement.value = this._value.toString();
      this._cd.markForCheck();
    }
    this.updateShowSelect();
  }

  onFocusIn() {
    this.propagateTouch();
  }

  private updateShowSelect() {
    this.showSelect = !this.forceNumberInput && this._value <= this.selectLimit;
    this._cd.detectChanges();
  }

  onRadioChange(changeProductId: number) {
    this.onChangeValue(1);
    Object.keys(QuantityInputComponent.RADIO_BUTTON_GROUP).forEach((groupName) => {
      const listener = QuantityInputComponent.RADIO_BUTTON_GROUP[groupName].listener;
      Object.keys(listener).forEach((productId) => listener[productId](changeProductId));
    });
  }

  onRadioGroupChange(productId: number) {
    const state = '' + this.product.product_id === '' + productId;
    this.checkboxState$.next(state);
    if (!state) {
      this.onChangeValue(0);
    }
    this._cd.detectChanges();
  }

  ngOnDestroy() {
    if (this.renderType === 'radiobutton') {
      delete QuantityInputComponent.RADIO_BUTTON_GROUP[this.radioButtonGroupName].listener[this.product.product_id];
    }

    this._destroy$.next();
    this._destroy$.complete();
  }

  ngOnChanges(changes: SimpleChanges) {
    this.initializeRenderType();
    this.initializeMax();
    this.initializeSelectValues();
  }
}
